// MainLayout.js — the shell every protected page renders inside.
//
// TARGET PATH: src/shared/layout/MainLayout.js
//
// Replicated from ecoh-ui-module's shared/layout/MainLayout.js, minus its
// resizable-sidebar machinery: this app's Navbar is a fixed-width sidebar driven
// by the --sidebar-width variable, so there is nothing to drag, collapse or
// measure. What is kept is the part that matters — one layout route holding the
// chrome, with <Outlet /> for the page.
//
// Before this existed, App.js repeated <SessionTimeout /> and <Navbar /> inside
// all sixteen protected routes. Beyond the duplication, that made it possible to
// add a route and silently forget the idle-timeout monitor.
//
// The boundary lives here rather than only at the top of App so that a crashing
// page leaves the sidebar intact — the user can navigate away instead of being
// stranded on an error screen.

import React, { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import SessionTimeout from '../components/SessionTimeout';
import Navbar from '../components/Navbar';
import TopBar from '../components/TopBar';
import ErrorBoundary from '../components/ErrorBoundary';
import LoadingSpinner from '../components/LoadingSpinner';

function MainLayout() {
  const location = useLocation();

  return (
    <>
      <SessionTimeout />
      <Navbar />
      <TopBar />
      {/*
        Keyed on the path so the boundary is a fresh instance per route. A
        boundary that has caught an error keeps showing its fallback until its
        state is reset, so without the key a crash on one page would follow the
        user to every page they navigated to next.
      */}
      <ErrorBoundary key={location.pathname} componentName="Page">
        {/*
          Route components are lazy (see App.js), so the page arrives one chunk
          at a time. The fallback sits inside the chrome rather than around it:
          the sidebar and top bar stay put while the chunk downloads, instead of
          the whole shell blanking on every navigation.

          It is also inside the boundary, not outside. lazyWithRetry handles a
          stale chunk by reloading once; if the chunk is still missing after
          that it rethrows, and the throw needs a boundary above it to land in.
        */}
        <Suspense fallback={<LoadingSpinner message="Loading page" />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default MainLayout;
